import { defineStore } from 'pinia'
import { useDbStore } from '@/stores/dbStore'
import i18n from '@/i18n'
import { translateEventTypeName } from '@/i18n/eventTypeVocab'

const t = (key, params) => i18n.global.t(`zooStore.${key}`, params)

export const useZooStore = defineStore('zoo', () => {
  const dbStore = useDbStore()

  // ==================== UTILITY ====================

  function _today() {
    return new Date().toISOString().split('T')[0]
  }

  // ==================== HERDS ====================

  async function getHerds(farmCenterId) {
    const result = await dbStore.select('herd')
    if (!result.ok) return []
    return result.data
      .filter(h => h.id_farm_center === farmCenterId)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }

  async function getHerdById(id_herd) {
    const result = await dbStore.selectOne('herd', id_herd)
    return result.ok ? result.data : null
  }

  async function saveHerd(herdData) {
    const data = { ...herdData }
    if (data.id_herd) {
      return await dbStore.update('herd', data.id_herd, data)
    } else {
      data.id_herd = dbStore.generateGuid()
      return await dbStore.insert('herd', data)
    }
  }

  /**
   * Herd di default per una specie in una farm center.
   * Prima cerca quello con default: true, poi il primo della specie.
   */
  async function getDefaultHerd(farmCenterId, specie) {
    const herds = await getHerds(farmCenterId)
    const forSpecie = herds.filter(h => h.specie === specie)
    return forSpecie.find(h => h.default) || forSpecie[0] || null
  }

  // ==================== ANIMALS ====================

  /**
   * Tutti gli animali attivi di un gregge (id_herd).
   * Carica tutto e filtra in JS — non usa indici Dexie.
   */
  async function getZooAnimals(id_herd) {
    const result = await dbStore.select('animal')
    if (!result.ok) return []
    return result.data.filter(
      a => a.id_herd === id_herd && !a.type_out
    ).sort((a, b) => {
      if (!a.date_birth) return 1
      if (!b.date_birth) return -1
      return new Date(b.date_birth) - new Date(a.date_birth)
    })
  }

  async function getAnimalById(id_animal) {
    const result = await dbStore.selectOne('animal', id_animal)
    return result.ok ? result.data : null
  }

  /**
   * Crea o aggiorna un animale.
   * @param {object} animalData
   * @param {object} [opts]
   * @param {boolean} [opts.isNew=false] - forzare insert anche se id_animal è già valorizzato
   *   (usato quando il GUID viene pre-generato nel form prima del primo salvataggio)
   */
  async function saveAnimal(animalData, { isNew = false } = {}) {
    const data = { ...animalData }

    // Pulizia: stringa vuota → null
    for (const key of Object.keys(data)) {
      if (data[key] === '' || data[key] === undefined) {
        data[key] = null
      }
    }

    if (!isNew && data.id_animal) {
      return await dbStore.update('animal', data.id_animal, data)
    } else {
      if (!data.id_animal) data.id_animal = dbStore.generateGuid()
      return await dbStore.insert('animal', data)
    }
  }

  /**
   * Registra l'uscita di un animale: imposta type_out + date_out.
   * L'animale NON viene soft-deleted: rimane in DB visibile come "uscito".
   */
  async function deleteAnimal(id_animal, type_out, date_out = null) {
    return await dbStore.update('animal', id_animal, {
      type_out,
      date_out: date_out || _today()
    })
  }

  /**
   * Ripristina un animale uscito: azzera type_out e date_out.
   */
  async function restoreAnimal(id_animal) {
    return await dbStore.update('animal', id_animal, {
      type_out: null,
      date_out: null
    })
  }

  /**
   * Eliminazione definitiva di un animale (solo se senza eventi né appartenenze a gruppi).
   * Imposta _deleted: true tramite dbStore.delete().
   */
  async function hardDeleteAnimal(id_animal) {
    // Blocca se ha eventi
    const evResult = await dbStore.select('event')
    const hasEvents = evResult.ok && evResult.data.some(e => e.id_animal === id_animal)
    if (hasEvents) {
      return { ok: false, message: t('cannotDeleteAnimalHasEvents') }
    }
    // Blocca se ha appartenenze a gruppi
    const membResult = await dbStore.select('animal_group_member')
    const hasMemberships = membResult.ok && membResult.data.some(m => m.id_animal === id_animal)
    if (hasMemberships) {
      return { ok: false, message: t('cannotDeleteAnimalHasMemberships') }
    }
    return await dbStore.delete('animal', id_animal)
  }

  // ==================== GROUPS ====================

  async function getAnimalGroups(id_herd) {
    const result = await dbStore.select('animal_group')
    if (!result.ok) return []
    return result.data
      .filter(g => g.id_herd === id_herd)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }

  async function getGroupById(id_animal_group) {

    console.log("group by id");
    const result = await dbStore.selectOne('animal_group', id_animal_group)
    console.log("get result", result);

    return result.ok ? result.data : null
  }

  async function saveAnimalGroup(groupData) {
    const data = { ...groupData }
    if (data.id_animal_group) {
      return await dbStore.update('animal_group', data.id_animal_group, data)
    } else {
      data.id_animal_group = dbStore.generateGuid()
      return await dbStore.insert('animal_group', data)
    }
  }

  /**
   * Elimina un gruppo (solo se non ha membri attivi).
   */
  async function deleteAnimalGroup(id_animal_group) {
    const members = await getGroupMembers(id_animal_group)
    if (members.length > 0) {
      return { ok: false, message: t('cannotDeleteGroupHasAnimals') }
    }
    return await dbStore.delete('animal_group', id_animal_group)
  }

  // ==================== MEMBERSHIPS ====================

  /**
   * Membri attivi di un gruppo (date_to === null), arricchiti con i dati dell'animale.
   */
  async function getGroupMembers(id_animal_group) {
    const group = await getGroupById(id_animal_group)
    if (!group) return []

    const membResult = await dbStore.select('animal_group_member')
    if (!membResult.ok) return []

    const activeMemberships = membResult.data.filter(
      m => m.id_animal_group === id_animal_group && !m.date_to
    )

    const animResult = await dbStore.select('animal')
    if (!animResult.ok) return []
    const animalMap = Object.fromEntries(animResult.data.map(a => [a.id_animal, a]))

    return activeMemberships
      .map(m => {
        const animal = animalMap[m.id_animal]
        if (!animal) return null
        return { ...animal, membership_id: m.id_animal_group_member, date_from: m.date_from, date_to: m.date_to }
      })
      .filter(Boolean)
  }

  async function countGroupMembers(id_animal_group) {
    const members = await getGroupMembers(id_animal_group)
    return members.length
  }

  /**
   * Gruppo corrente di un animale (membership con date_to === null).
   */
  async function getAnimalCurrentGroup(id_animal) {
    const result = await dbStore.select('animal_group_member')
    if (!result.ok) return null

    const active = result.data.find(
      m => m.id_animal === id_animal && !m.date_to
    )
    if (!active) return null

    const group = await getGroupById(active.id_animal_group)
    if (!group) return null

    return { ...group, membership_id: active.id_animal_group_member, date_from: active.date_from, date_to: active.date_to }
  }

  /**
   * Storico completo dei gruppi di un animale, ordinato per data discendente.
   */
  async function getAnimalGroupHistory(id_animal) {
    const membResult = await dbStore.select('animal_group_member')
    if (!membResult.ok) return []

    const memberships = membResult.data.filter(m => m.id_animal === id_animal)

    const groupResult = await dbStore.select('animal_group')
    const groupMap = groupResult.ok
      ? Object.fromEntries(groupResult.data.map(g => [g.id_animal_group, g]))
      : {}

    return memberships
      .map(m => {
        const group = groupMap[m.id_animal_group]
        if (!group) return null
        return {
          id_animal_group_member: m.id_animal_group_member,
          id_animal_group: m.id_animal_group,
          group_name: group.name,
          group_color: group.color,
          date_from: m.date_from,
          date_to: m.date_to,
          is_current: !m.date_to
        }
      })
      .filter(Boolean)
      .sort((a, b) => new Date(b.date_from) - new Date(a.date_from))
  }

  /**
   * Sposta animali da un gruppo all'altro.
   * Chiude le membership precedenti e apre le nuove.
   * @param {string[]} animalIds
   * @param {string|null} targetGroupId - null per rimuovere da tutti i gruppi
   * @param {string|null} moveDate - YYYY-MM-DD, default oggi
   */
  async function moveAnimalsToGroup(animalIds, targetGroupId, moveDate = null) {
    const effectiveDate = moveDate || _today()
    let ok = true
    const operations = []

    const membResult = await dbStore.select('animal_group_member')
    const allMemberships = membResult.ok ? membResult.data : []

    // Recupera id_farm_center dal primo animale
    let id_farm_center = null
    if (targetGroupId) {
      const firstAnimal = await getAnimalById(animalIds[0])
      id_farm_center = firstAnimal?.id_farm_center || null
    }

    for (const id_animal of animalIds) {
      // 1. Chiudi membership attive
      const activeMemberships = allMemberships.filter(
        m => m.id_animal === id_animal && !m.date_to
      )
      for (const m of activeMemberships) {
        const res = await dbStore.update('animal_group_member', m.id_animal_group_member, {
          date_to: effectiveDate
        })
        if (!res.ok) ok = false
        operations.push({ type: 'close', id_animal, id_animal_group: m.id_animal_group, result: res })
      }

      // 2. Crea nuova membership
      if (targetGroupId) {
        if (!id_farm_center) {
          const a = await getAnimalById(id_animal)
          id_farm_center = a?.id_farm_center || null
        }
        const newId = dbStore.generateGuid()
        const res = await dbStore.insert('animal_group_member', {
          id_animal_group_member: newId,
          id_farm_center,
          id_animal,
          id_animal_group: targetGroupId,
          date_from: effectiveDate,
          date_to: null
        })
        if (!res.ok) ok = false
        operations.push({ type: 'insert', id_animal, id_animal_group: targetGroupId, result: res })
      }
    }

    return {
      ok,
      operations,
      moveDate: effectiveDate,
      message: ok
        ? t('animalsMoved', { count: animalIds.length })
        : t('errorDuringMove')
    }
  }

  async function deleteGroupMember(id_animal_group_member) {
    return await dbStore.delete('animal_group_member', id_animal_group_member)
  }

  // ==================== EVENTS ====================

  /**
   * Eventi di un singolo animale, ordinati per data discendente.
   * Gli eventi sono in tabella 'event', non embedded in animal.
   */
  async function getAnimalEvents(id_animal) {
    const [evResult, cfResult] = await Promise.all([
      dbStore.select('event'),
      dbStore.select('animal_custom_form'),
    ])
    if (!evResult.ok) return []
    const cfMap = Object.fromEntries(
      (cfResult.ok ? cfResult.data : []).map(f => [f.id_animal_custom_form, translateEventTypeName(f.form_name)])
    )
    return evResult.data
      .filter(e => e.id_animal === id_animal && !e.id_animal_group)
      .map(e => ({ ...e, custom_form_name: cfMap[e.id_animal_custom_form] || null }))
      .sort((a, b) => new Date(b.date) - new Date(a.date))
  }

  async function saveAnimalEvent(eventData, { isNew = false } = {}) {
    const data = { ...eventData }
    if (!isNew && data.id_event) {
      return await dbStore.update('event', data.id_event, data)
    } else {
      if (!data.id_event) data.id_event = dbStore.generateGuid()
      return await dbStore.insert('event', data)
    }
  }

  async function deleteAnimalEvent(id_event) {
    return await dbStore.delete('event', id_event)
  }

  /**
   * Eventi dei gruppi a cui l'animale apparteneva, filtrati per il periodo di membership.
   * Un evento di gruppo compare nella timeline dell'animale solo se la sua data
   * ricade nell'intervallo [date_from, date_to] della membership corrispondente.
   */
  async function getAnimalGroupEvents(id_animal) {
    const membResult = await dbStore.select('animal_group_member')
    if (!membResult.ok) return []
    const memberships = membResult.data.filter(m => m.id_animal === id_animal)
    if (!memberships.length) return []

    const [evResult, cfResult, groupResult] = await Promise.all([
      dbStore.select('event'),
      dbStore.select('animal_custom_form'),
      dbStore.select('animal_group'),
    ])
    if (!evResult.ok) return []

    const cfMap = Object.fromEntries(
      (cfResult.ok ? cfResult.data : []).map(f => [f.id_animal_custom_form, translateEventTypeName(f.form_name)])
    )
    const groupMap = Object.fromEntries(
      (groupResult.ok ? groupResult.data : []).map(g => [g.id_animal_group, g])
    )

    const groupIds = new Set(memberships.map(m => m.id_animal_group))
    const results = []

    for (const ev of evResult.data) {
      if (!ev.id_animal_group || !groupIds.has(ev.id_animal_group)) continue
      // Verifica che la data dell'evento ricada in almeno una membership
      const inPeriod = memberships.some(m => {
        if (m.id_animal_group !== ev.id_animal_group) return false
        if (!ev.date || ev.date < m.date_from) return false
        if (m.date_to && ev.date > m.date_to) return false
        return true
      })
      if (!inPeriod) continue
      const group = groupMap[ev.id_animal_group]
      results.push({
        ...ev,
        custom_form_name: cfMap[ev.id_animal_custom_form] || null,
        _group_name: group?.name || null,
        _group_color: group?.color || '#888',
        _is_group_event: true,
      })
    }

    return results.sort((a, b) => new Date(b.date) - new Date(a.date))
  }

  /**
   * Eventi di un gruppo, ordinati per data discendente.
   */
  async function getGroupEvents(id_animal_group) {
    const [evResult, cfResult] = await Promise.all([
      dbStore.select('event'),
      dbStore.select('animal_custom_form'),
    ])
    if (!evResult.ok) return []
    const cfMap = Object.fromEntries(
      (cfResult.ok ? cfResult.data : []).map(f => [f.id_animal_custom_form, translateEventTypeName(f.form_name)])
    )


    return evResult.data
      .filter(e => e.id_animal_group === id_animal_group)
      .map(e => ({ ...e, custom_form_name: cfMap[e.id_animal_custom_form] || null }))
      .sort((a, b) => new Date(b.date) - new Date(a.date))
  }

  async function saveGroupEvent(eventData, { isNew = false } = {}) {
    const data = { ...eventData }
    if (!isNew && data.id_event) {
      return await dbStore.update('event', data.id_event, data)
    } else {
      if (!data.id_event) data.id_event = dbStore.generateGuid()
      return await dbStore.insert('event', data)
    }
  }

  async function deleteGroupEvent(id_event) {
    return await dbStore.delete('event', id_event)
  }

  // ==================== STATISTICS ====================

  async function getHerdStatistics(id_herd) {
    const animals = await getZooAnimals(id_herd)
    const stats = { m_adult: 0, f_adult: 0, m_baby: 0, f_baby: 0, unclassified: 0, total: animals.length }
    const today = new Date()

    for (const a of animals) {
      if (!a.date_birth) {
        stats.unclassified++
        continue
      }
      const ageMonths = (today - new Date(a.date_birth)) / (1000 * 60 * 60 * 24 * 30)
      const isAdult = ageMonths > 6
      if (a.sex === 'M') isAdult ? stats.m_adult++ : stats.m_baby++
      else if (a.sex === 'F') isAdult ? stats.f_adult++ : stats.f_baby++
    }
    return stats
  }

  async function getBirthsInPeriod(id_herd, months) {
    const cutoff = new Date()
    cutoff.setMonth(cutoff.getMonth() - months)
    const cutoffStr = cutoff.toISOString().split('T')[0]

    const animals = await getZooAnimals(id_herd)
    return animals.filter(
      a => a.type_in === 'N' && a.date_birth && a.date_birth >= cutoffStr
    ).length
  }

  // ==================== CUSTOM FORMS ====================

  /**
   * Carica i tipi evento da animal_custom_form.
   * @param {string|null} specie  - filtra per specie
   * @param {string|null} valid_for - 'M' (maschi), 'F' (femmine), 'G' (gruppi)
   */
  async function getAnimalCustomForms(specie = null, valid_for = null) {
    const result = await dbStore.select('animal_custom_form')
    if (!result.ok) return []
    let data = result.data.filter(f => f.active !== 0)
    if (specie) data = data.filter(f => f.specie === specie)
    if (valid_for) data = data.filter(f => f.valid_for === valid_for)
    return data
      .map(f => ({ ...f, form_name: translateEventTypeName(f.form_name) }))
      .sort((a, b) => (a.order_n || 0) - (b.order_n || 0))
  }

  // ==================== BREEDS ====================

  /**
   * Razze dalla tabella breed (vocabolario, non sincronizzata).
   * Filtra per specie se fornita.
   */
  async function getBreeds(specie = null) {
    const result = await dbStore.select('breed')
    if (!result.ok) return []
    const data = specie ? result.data.filter(b => b.specie === specie) : result.data
    return data.sort((a, b) => (a.breed || '').localeCompare(b.breed || ''))
  }

  /**
   * Animali usciti di un gregge (type_out impostato).
   */
  async function getExitedAnimals(id_herd) {
    const result = await dbStore.select('animal')
    if (!result.ok) return []
    return result.data.filter(
      a => a.id_herd === id_herd && a.type_out
    ).sort((a, b) => {
      if (!a.date_out) return 1
      if (!b.date_out) return -1
      return new Date(b.date_out) - new Date(a.date_out)
    })
  }

  /**
   * Conta quante volte un code_bdn è stato letto via Bluetooth oggi in
   * un'azienda (tabella log_bluetooth). Usato per avvisare l'utente di
   * una possibile doppia lettura dello stesso bolo.
   */
  async function getBluetoothScanCountToday(idFarmCenter, codBdn) {
    if (!codBdn) return 0
    const result = await dbStore.select('log_bluetooth')
    if (!result.ok) return 0
    const today = _today()
    return result.data.filter(
      l => l.id_farm_center === idFarmCenter && l.cod_bdn === codBdn && (l.datetime || '').startsWith(today)
    ).length
  }

  return {
    // Herds
    getHerds,
    getHerdById,
    saveHerd,
    getDefaultHerd,
    // Animals
    getZooAnimals,
    getAnimalById,
    saveAnimal,
    deleteAnimal,
    restoreAnimal,
    hardDeleteAnimal,
    // Groups
    getAnimalGroups,
    getGroupById,
    saveAnimalGroup,
    deleteAnimalGroup,
    // Memberships
    getGroupMembers,
    countGroupMembers,
    getAnimalCurrentGroup,
    getAnimalGroupHistory,
    moveAnimalsToGroup,
    deleteGroupMember,
    // Events
    getAnimalEvents,
    getAnimalGroupEvents,
    saveAnimalEvent,
    deleteAnimalEvent,
    getGroupEvents,
    saveGroupEvent,
    deleteGroupEvent,
    // Statistics
    getHerdStatistics,
    getBirthsInPeriod,
    // Custom forms
    getAnimalCustomForms,
    // Breeds
    getBreeds,
    getExitedAnimals,
    // Bluetooth
    getBluetoothScanCountToday,
  }
})
